import React, { useState, useEffect, useRef } from 'react';
import api, { facilityAPI } from '../services/api';
import { Plus, Trash2, Leaf, Zap, Globe, RefreshCw, Upload, CheckCircle, AlertTriangle } from 'lucide-react';
import { toast } from 'react-toastify';

const SCOPE3_CATEGORIES = [
  { num: 1, name: 'Purchased Goods & Services' },
  { num: 2, name: 'Capital Goods' },
  { num: 3, name: 'Fuel & Energy Related' },
  { num: 4, name: 'Upstream Transportation' },
  { num: 5, name: 'Waste Generated in Operations' },
  { num: 6, name: 'Business Travel' },
  { num: 7, name: 'Employee Commuting' },
  { num: 8, name: 'Upstream Leased Assets' },
  { num: 9, name: 'Downstream Transportation' },
  { num: 10, name: 'Processing of Sold Products' },
  { num: 11, name: 'Use of Sold Products' },
  { num: 12, name: 'End-of-Life Treatment' },
  { num: 13, name: 'Downstream Leased Assets' },
  { num: 14, name: 'Franchises' },
  { num: 15, name: 'Investments' },
];

const EMISSION_TYPES_S1 = [
  { value: 'stationary_combustion', label: 'Stationary Combustion (boilers, furnaces)' },
  { value: 'mobile_combustion', label: 'Mobile Combustion (fleet vehicles)' },
  { value: 'process', label: 'Process Emissions' },
  { value: 'fugitive', label: 'Fugitive Emissions (refrigerants, leaks)' },
];

const EMISSION_TYPES_S2 = [
  { value: 'electricity', label: 'Purchased Electricity' },
  { value: 'steam', label: 'Purchased Steam' },
  { value: 'heat', label: 'Purchased Heat' },
  { value: 'cooling', label: 'Purchased Cooling' },
];

const DATA_QUALITY = [
  { value: 'measured', label: 'Measured' },
  { value: 'calculated', label: 'Calculated' },
  { value: 'estimated', label: 'Estimated' },
  { value: 'supplier_provided', label: 'Supplier Provided' },
];

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

function GHGInventory() {
  const [activeScope, setActiveScope] = useState(1);
  const [emissions, setEmissions] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [factors, setFactors] = useState([]);
  const [egrid, setEgrid] = useState([]);
  const [settings, setSettings] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showBillUpload, setShowBillUpload] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [billExtracted, setBillExtracted] = useState(null);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(new Date().getFullYear());
  const fileRef = useRef();

  const [form, setForm] = useState({
    facility_id: '', emission_type: '', activity_data: '', activity_unit: 'MMBtu',
    ghg_factor_id: '', egrid_id: '', co2e_mt: '', data_quality: 'calculated',
    methodology_note: '', month: new Date().getMonth() + 1, year: new Date().getFullYear(), notes: ''
  });

  useEffect(() => { loadAll(); }, [year, activeScope]);

  // When facility changes, auto-set eGRID subregion
  useEffect(() => {
    if (form.facility_id && activeScope === 2) {
      api.get(`/ghg/egrid-for-facility/${form.facility_id}`)
        .then(res => {
          if (res.data.egrid) {
            setForm(f => ({
              ...f,
              egrid_id: res.data.egrid.id,
              methodology_note: `Location-based Scope 2 — ${res.data.egrid.subregion_code} eGRID subregion (${res.data.state}) — ${res.data.egrid.co2e_lb_per_mwh} lb CO2e/MWh`
            }));
          }
        }).catch(() => {});
    }
  }, [form.facility_id, activeScope]);

  const loadAll = async () => {
    try {
      setLoading(true);
      const [emRes, facRes, facRes2, egridRes, settingsRes] = await Promise.all([
        api.get(`/ghg/emissions?year=${year}&scope=${activeScope}`),
        facilityAPI.getAll(),
        api.get('/ghg/factors'),
        api.get('/ghg/egrid'),
        api.get('/ghg/settings'),
      ]);
      setEmissions(emRes.data.emissions || []);
      setFacilities(facRes.data.facilities || []);
      setFactors(facRes2.data.factors || []);
      setEgrid(egridRes.data.subregions || []);
      setSettings(settingsRes.data.settings);
    } catch (e) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Auto-calculate CO2e when activity data or factor changes
  const calcCO2e = (activityData, factorId, egridId) => {
    const qty = parseFloat(activityData);
    if (!qty) return '';
    if (factorId) {
      const f = factors.find(x => x.id === parseInt(factorId));
      if (f) return ((qty * parseFloat(f.co2e_factor)) / 1000).toFixed(6);
    }
    if (egridId) {
      const e = egrid.find(x => x.id === parseInt(egridId));
      if (e) return ((qty * parseFloat(e.co2e_lb_per_mwh)) / (1000 * 2204.62)).toFixed(6);
    }
    return '';
  };

  const autoMethodologyNote = (factorId, egridId, emissionType) => {
    if (factorId) {
      const f = factors.find(x => x.id === parseInt(factorId));
      if (f) return `EPA ${f.category} emission factor — ${f.fuel_or_source} — ${f.co2e_factor} kg CO2e/${f.unit}`;
    }
    if (egridId) {
      const e = egrid.find(x => x.id === parseInt(egridId));
      if (e) return `Location-based Scope 2 — ${e.subregion_code} eGRID subregion — ${e.co2e_lb_per_mwh} lb CO2e/MWh`;
    }
    return '';
  };

  const handleAutoSync = async () => {
    if (!window.confirm(`Auto-sync Scope 1 emissions from your existing emissions log for ${year}? This will calculate CO2e for all sources with fuel usage data.`)) return;
    try {
      setSyncing(true);
      const res = await api.post('/ghg/auto-sync-scope1', { year });
      toast.success(`✅ Synced ${res.data.synced} entries — ${res.data.skipped} already existed`);
      loadAll();
    } catch (e) {
      toast.error('Sync failed — make sure sources have fuel type and fuel usage data');
    } finally {
      setSyncing(false);
    }
  };

  const handleBillUpload = async (file) => {
    if (!file) return;
    try {
      setExtracting(true);
      setBillExtracted(null);
      // Read file as text
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const text = e.target.result;
          const res = await api.post('/ghg/extract-utility-bill', {
            text,
            facility_id: form.facility_id || null
          });
          setBillExtracted(res.data);
          // Auto-fill form
          const ext = res.data.extracted;
          setForm(f => ({
            ...f,
            month: ext.month || f.month,
            year: ext.year || f.year,
            activity_data: ext.electricity_kwh || ext.natural_gas_mmbtu || '',
            activity_unit: ext.electricity_kwh ? 'kWh' : 'MMBtu',
            emission_type: ext.utility_type === 'natural_gas' ? 'stationary_combustion' : 'electricity',
            co2e_mt: ext.electricity_kwh ? res.data.electricity_co2e_mt || '' : res.data.natural_gas_co2e_mt || '',
            methodology_note: res.data.egrid
              ? `Location-based Scope 2 — ${res.data.egrid.subregion_code} eGRID — ${res.data.egrid.co2e_lb_per_mwh} lb CO2e/MWh — Extracted from utility bill`
              : 'Extracted from utility bill via AI',
            egrid_id: res.data.egrid?.id || f.egrid_id,
          }));
          toast.success('Bill data extracted — review and save');
          setShowBillUpload(false);
          setShowForm(true);
        } catch (err) {
          toast.error('Failed to extract bill data');
        } finally {
          setExtracting(false);
        }
      };
      reader.readAsText(file);
    } catch (e) {
      toast.error('Failed to read file');
      setExtracting(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const co2e = form.co2e_mt || calcCO2e(form.activity_data, form.ghg_factor_id, form.egrid_id);
      const note = form.methodology_note || autoMethodologyNote(form.ghg_factor_id, form.egrid_id, form.emission_type);
      await api.post('/ghg/emissions', {
        ...form,
        scope: activeScope,
        co2e_mt: co2e,
        ghg_factor_id: form.ghg_factor_id || null,
        scope3_category: activeScope === 3 ? form.scope3_category : null,
        methodology_note: note,
      });
      toast.success('Emission logged');
      setShowForm(false);
      setForm({ facility_id: '', emission_type: '', activity_data: '', activity_unit: 'MMBtu', ghg_factor_id: '', egrid_id: '', co2e_mt: '', data_quality: 'calculated', methodology_note: '', month: new Date().getMonth() + 1, year: new Date().getFullYear(), notes: '' });
      loadAll();
    } catch (e) {
      toast.error('Failed to save');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this entry?')) return;
    await api.delete(`/ghg/emissions/${id}`);
    loadAll();
  };

  const filteredEmissions = emissions.filter(e => e.scope === activeScope);
  const totalCO2e = filteredEmissions.reduce((sum, e) => sum + parseFloat(e.co2e_mt || 0), 0);
  const relevantFactors = factors.filter(f =>
    activeScope === 1 ? ['stationary_combustion', 'mobile_combustion', 'fugitive'].includes(f.category) : true
  );

  const scopeTabs = [
    { scope: 1, label: 'Scope 1', sublabel: 'Direct emissions', icon: Leaf, color: '#0066FF' },
    { scope: 2, label: 'Scope 2', sublabel: 'Purchased energy', icon: Zap, color: '#16A34A' },
    { scope: 3, label: 'Scope 3', sublabel: 'Value chain', icon: Globe, color: '#D97706' },
  ];

  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 400, color: '#6B7280' }}>Loading...</div>;

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontFamily: 'var(--font-heading)', fontSize: 22, fontWeight: 800, color: '#0A1628', margin: '0 0 4px' }}>Emissions Inventory</h1>
          <div style={{ fontSize: 14, color: '#6B7280', fontFamily: 'var(--font-body)' }}>Log and track Scope 1, 2, and 3 greenhouse gas emissions</div>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'nowrap' }}>
          <select value={year} onChange={e => setYear(parseInt(e.target.value))}
            style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #E2E8F0', fontFamily: 'var(--font-body)', fontSize: 14, flexShrink: 0 }}>
            {[2022,2023,2024,2025,2026].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          {activeScope === 1 && (
            <button onClick={handleAutoSync} disabled={syncing}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: '1px solid #0066FF', background: '#EFF6FF', color: '#0066FF', fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: 600, cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap' }}>
              <RefreshCw size={14} style={{ animation: syncing ? 'spin 1s linear infinite' : 'none' }} />
              {syncing ? 'Syncing...' : 'Auto-sync from Log'}
            </button>
          )}
          {activeScope === 2 && (
            <button onClick={() => setShowBillUpload(true)}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: '1px solid #16A34A', background: '#F0FDF4', color: '#16A34A', fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: 600, cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap' }}>
              <Upload size={14} /> Upload Utility Bill
            </button>
          )}
          <button onClick={() => setShowForm(!showForm)} className="btn btn-primary" style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0, whiteSpace: 'nowrap' }}>
            <Plus size={14} /> Log Manually
          </button>
        </div>
      </div>

      {/* Scope tabs */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
        {scopeTabs.map(tab => (
          <button key={tab.scope} onClick={() => { setActiveScope(tab.scope); setShowForm(false); setShowBillUpload(false); }}
            style={{ flex: 1, padding: '12px 16px', borderRadius: 10, border: `2px solid ${activeScope === tab.scope ? tab.color : '#E2E8F0'}`, background: activeScope === tab.scope ? (tab.scope === 1 ? '#EFF6FF' : tab.scope === 2 ? '#F0FDF4' : '#FFFBEB') : 'white', cursor: 'pointer', textAlign: 'left' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <tab.icon size={16} color={tab.color} />
              <span style={{ fontFamily: 'var(--font-heading)', fontSize: 14, fontWeight: 700, color: activeScope === tab.scope ? tab.color : '#374151' }}>{tab.label}</span>
            </div>
            <div style={{ fontSize: 11, color: '#6B7280', fontFamily: 'var(--font-body)' }}>{tab.sublabel}</div>
          </button>
        ))}
      </div>

      {/* Scope 1 auto-sync banner */}
      {activeScope === 1 && filteredEmissions.length === 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 18px', background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 10, marginBottom: 20 }}>
          <RefreshCw size={18} color="#0066FF" />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#1D4ED8', fontFamily: 'var(--font-body)' }}>Auto-sync available</div>
            <div style={{ fontSize: 13, color: '#3B82F6', fontFamily: 'var(--font-body)' }}>Your existing emissions log has data — click "Auto-sync from Emissions Log" to automatically calculate Scope 1 CO2e. No manual entry needed.</div>
          </div>
          <button onClick={handleAutoSync} disabled={syncing} className="btn btn-primary" style={{ flexShrink: 0 }}>
            {syncing ? 'Syncing...' : 'Auto-sync Now'}
          </button>
        </div>
      )}

      {/* Utility bill upload */}
      {showBillUpload && (
        <div className="card" style={{ padding: '20px', marginBottom: 20, border: '2px solid #16A34A' }}>
          <div style={{ fontFamily: 'var(--font-heading)', fontSize: 15, fontWeight: 700, color: '#0A1628', marginBottom: 8 }}>Upload Utility Bill</div>
          <div style={{ fontSize: 13, color: '#6B7280', fontFamily: 'var(--font-body)', marginBottom: 16 }}>
            Upload a utility bill (PDF or text) — AI will extract kWh usage, billing period, and calculate CO2e automatically using your facility's eGRID subregion.
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', fontFamily: 'var(--font-body)', display: 'block', marginBottom: 4 }}>Select Facility (for eGRID auto-detection)</label>
            <select value={form.facility_id} onChange={e => setForm({...form, facility_id: e.target.value})}
              style={{ width: 300, padding: '8px 12px', borderRadius: 8, border: '1px solid #E2E8F0', fontFamily: 'var(--font-body)', fontSize: 14 }}>
              <option value="">Select facility...</option>
              {facilities.map(f => <option key={f.id} value={f.id}>{f.facility_name || f.name}</option>)}
            </select>
          </div>
          <input ref={fileRef} type="file" accept=".pdf,.txt,.csv" style={{ display: 'none' }}
            onChange={e => handleBillUpload(e.target.files[0])} />
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={() => fileRef.current.click()} disabled={extracting}
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 20px', borderRadius: 8, border: '2px dashed #16A34A', background: '#F0FDF4', color: '#15803D', fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
              <Upload size={16} /> {extracting ? 'Extracting with AI...' : 'Choose Bill File'}
            </button>
            <button onClick={() => setShowBillUpload(false)}
              style={{ padding: '12px 20px', borderRadius: 8, border: '1px solid #E2E8F0', background: 'white', cursor: 'pointer', fontFamily: 'var(--font-body)', fontSize: 14 }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Manual entry form */}
      {showForm && (
        <div className="card" style={{ padding: '20px', marginBottom: 20, border: '2px solid #0066FF' }}>
          <div style={{ fontFamily: 'var(--font-heading)', fontSize: 15, fontWeight: 700, color: '#0A1628', marginBottom: 16 }}>
            Log Scope {activeScope} Emission {billExtracted && <span style={{ fontSize: 12, fontWeight: 500, color: '#16A34A', marginLeft: 8 }}>✓ Pre-filled from utility bill</span>}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 14 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', fontFamily: 'var(--font-body)', display: 'block', marginBottom: 4 }}>Facility</label>
              <select value={form.facility_id} onChange={e => setForm({...form, facility_id: e.target.value})}
                style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #E2E8F0', fontFamily: 'var(--font-body)', fontSize: 14 }}>
                <option value="">Select facility</option>
                {facilities.map(f => <option key={f.id} value={f.id}>{f.facility_name || f.name}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', fontFamily: 'var(--font-body)', display: 'block', marginBottom: 4 }}>Emission Type</label>
              <select value={form.emission_type} onChange={e => setForm({...form, emission_type: e.target.value})}
                style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #E2E8F0', fontFamily: 'var(--font-body)', fontSize: 14 }}>
                <option value="">Select type</option>
                {(activeScope === 1 ? EMISSION_TYPES_S1 : activeScope === 2 ? EMISSION_TYPES_S2 : SCOPE3_CATEGORIES.map(c => ({ value: `cat${c.num}`, label: c.name }))).map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', fontFamily: 'var(--font-body)', display: 'block', marginBottom: 4 }}>Month / Year</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <select value={form.month} onChange={e => setForm({...form, month: parseInt(e.target.value)})}
                  style={{ flex: 1, padding: '8px 12px', borderRadius: 8, border: '1px solid #E2E8F0', fontFamily: 'var(--font-body)', fontSize: 14 }}>
                  {MONTHS.map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
                </select>
                <select value={form.year} onChange={e => setForm({...form, year: parseInt(e.target.value)})}
                  style={{ width: 80, padding: '8px 12px', borderRadius: 8, border: '1px solid #E2E8F0', fontFamily: 'var(--font-body)', fontSize: 14 }}>
                  {[2022,2023,2024,2025,2026].map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 14, marginBottom: 14 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', fontFamily: 'var(--font-body)', display: 'block', marginBottom: 4 }}>Activity Data</label>
              <input type="number" value={form.activity_data}
                onChange={e => {
                  const val = e.target.value;
                  const co2e = calcCO2e(val, form.ghg_factor_id, form.egrid_id);
                  const note = autoMethodologyNote(form.ghg_factor_id, form.egrid_id, form.emission_type);
                  setForm({...form, activity_data: val, co2e_mt: co2e, methodology_note: form.methodology_note || note });
                }}
                placeholder="0.00" style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #E2E8F0', fontFamily: 'var(--font-body)', fontSize: 14, boxSizing: 'border-box' }} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', fontFamily: 'var(--font-body)', display: 'block', marginBottom: 4 }}>Unit</label>
              <select value={form.activity_unit} onChange={e => setForm({...form, activity_unit: e.target.value})}
                style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #E2E8F0', fontFamily: 'var(--font-body)', fontSize: 14 }}>
                {['MMBtu','gallon','kWh','MWh','ton','lb','mile','km','tonne','therm','CCF'].map(u => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', fontFamily: 'var(--font-body)', display: 'block', marginBottom: 4 }}>
                {activeScope === 2 ? 'eGRID Subregion' : 'Emission Factor'}
                {activeScope === 2 && form.egrid_id && <span style={{ color: '#16A34A', marginLeft: 4 }}>✓ Auto-detected</span>}
              </label>
              {activeScope === 2 ? (
                <select value={form.egrid_id} onChange={e => {
                  const co2e = calcCO2e(form.activity_data, null, e.target.value);
                  const note = autoMethodologyNote(null, e.target.value, form.emission_type);
                  setForm({...form, egrid_id: e.target.value, co2e_mt: co2e, methodology_note: note});
                }}
                  style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: `1px solid ${form.egrid_id ? '#16A34A' : '#E2E8F0'}`, fontFamily: 'var(--font-body)', fontSize: 14 }}>
                  <option value="">Select subregion</option>
                  {egrid.map(e => <option key={e.id} value={e.id}>{e.subregion_code} — {e.subregion_name}</option>)}
                </select>
              ) : (
                <select value={form.ghg_factor_id} onChange={e => {
                  const co2e = calcCO2e(form.activity_data, e.target.value, null);
                  const note = autoMethodologyNote(e.target.value, null, form.emission_type);
                  setForm({...form, ghg_factor_id: e.target.value, co2e_mt: co2e, methodology_note: note});
                }}
                  style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #E2E8F0', fontFamily: 'var(--font-body)', fontSize: 14 }}>
                  <option value="">Select factor</option>
                  {relevantFactors.map(f => <option key={f.id} value={f.id}>{f.fuel_or_source} ({f.co2e_factor} kg CO2e/{f.unit})</option>)}
                </select>
              )}
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', fontFamily: 'var(--font-body)', display: 'block', marginBottom: 4 }}>
                CO2e (mt)
                {form.activity_data && (form.ghg_factor_id || form.egrid_id) && <span style={{ color: '#16A34A', marginLeft: 4, fontWeight: 500 }}>✓ Auto-calculated</span>}
              </label>
              <input type="number" value={form.co2e_mt} onChange={e => setForm({...form, co2e_mt: e.target.value})}
                placeholder="Auto-calculated"
                style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: `1px solid ${form.co2e_mt ? '#16A34A' : '#E2E8F0'}`, fontFamily: 'var(--font-body)', fontSize: 14, boxSizing: 'border-box', background: form.co2e_mt ? '#F0FDF4' : 'white' }} />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 14, marginBottom: 14 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', fontFamily: 'var(--font-body)', display: 'block', marginBottom: 4 }}>Data Quality</label>
              <select value={form.data_quality} onChange={e => setForm({...form, data_quality: e.target.value})}
                style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #E2E8F0', fontFamily: 'var(--font-body)', fontSize: 14 }}>
                {DATA_QUALITY.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', fontFamily: 'var(--font-body)', display: 'block', marginBottom: 4 }}>
                Methodology Note
                {form.methodology_note && <span style={{ color: '#16A34A', marginLeft: 4, fontWeight: 500 }}>✓ Auto-generated</span>}
              </label>
              <input type="text" value={form.methodology_note} onChange={e => setForm({...form, methodology_note: e.target.value})}
                style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: `1px solid ${form.methodology_note ? '#16A34A' : '#E2E8F0'}`, fontFamily: 'var(--font-body)', fontSize: 14, boxSizing: 'border-box', background: form.methodology_note ? '#F0FDF4' : 'white' }} />
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button onClick={() => { setShowForm(false); setBillExtracted(null); }}
              style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #E2E8F0', background: 'white', cursor: 'pointer', fontFamily: 'var(--font-body)', fontSize: 14 }}>Cancel</button>
            <button onClick={handleSubmit} className="btn btn-primary">Save Entry</button>
          </div>
        </div>
      )}

      {/* Summary */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div style={{ fontSize: 14, color: '#374151', fontFamily: 'var(--font-body)' }}>
          <span style={{ fontWeight: 700 }}>{filteredEmissions.length}</span> entries · <span style={{ fontWeight: 700, color: '#0066FF' }}>{totalCO2e.toFixed(4)} mt CO2e</span>
        </div>
      </div>

      {/* Table */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#F8FAFC' }}>
              {['Month/Year','Facility','Type','Activity','CO2e (mt)','Data Quality','Methodology','Actions'].map(h => (
                <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#6B7280', fontFamily: 'var(--font-body)', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid #F1F5F9' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredEmissions.length === 0 ? (
              <tr>
                <td colSpan={8} style={{ padding: '40px', textAlign: 'center', color: '#94a3b8', fontFamily: 'var(--font-body)', fontSize: 14 }}>
                  {activeScope === 1 ? 'Use "Auto-sync from Emissions Log" to import existing data, or log manually.' : activeScope === 2 ? 'Upload a utility bill or log manually.' : 'Log Scope 3 emissions manually.'}
                </td>
              </tr>
            ) : filteredEmissions.map(e => (
              <tr key={e.id} style={{ borderBottom: '1px solid #F8FAFC' }}
                onMouseEnter={ev => ev.currentTarget.style.background = '#FAFAFA'}
                onMouseLeave={ev => ev.currentTarget.style.background = 'white'}>
                <td style={{ padding: '10px 14px', fontSize: 13, fontFamily: 'var(--font-body)', color: '#374151' }}>{MONTHS[e.month-1]?.slice(0,3)} {e.year}</td>
                <td style={{ padding: '10px 14px', fontSize: 13, fontFamily: 'var(--font-body)', color: '#374151' }}>{e.facility_name || '—'}</td>
                <td style={{ padding: '10px 14px', fontSize: 13, fontFamily: 'var(--font-body)', color: '#374151' }}>{e.emission_type?.replace(/_/g,' ').replace(/\b\w/g,l=>l.toUpperCase())}</td>
                <td style={{ padding: '10px 14px', fontSize: 13, fontFamily: 'var(--font-body)', color: '#374151' }}>{e.activity_data} {e.activity_unit}</td>
                <td style={{ padding: '10px 14px', fontSize: 13, fontWeight: 700, fontFamily: 'var(--font-body)', color: '#0066FF' }}>{parseFloat(e.co2e_mt||0).toFixed(4)}</td>
                <td style={{ padding: '10px 14px' }}>
                  <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 20,
                    background: e.data_quality==='measured'?'#F0FDF4':e.data_quality==='calculated'?'#EFF6FF':'#FFFBEB',
                    color: e.data_quality==='measured'?'#16A34A':e.data_quality==='calculated'?'#0066FF':'#D97706' }}>
                    {e.data_quality}
                  </span>
                </td>
                <td style={{ padding: '10px 14px', fontSize: 11, color: '#6B7280', fontFamily: 'var(--font-body)', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {e.methodology_note || '—'}
                </td>
                <td style={{ padding: '10px 14px' }}>
                  <button onClick={() => handleDelete(e.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#DC2626', padding: 4 }}>
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

export default GHGInventory;
